import React, { FC, ReactNode, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Popover } from "@mui/material";
import { ExpertRangeCalendar, DayRange } from "../../../../../components/FilmingScheduling/ExpertRangeCalendar";
import { Button } from "../../../../../components/Button/Button";
import { ChartRange, rangeFloor } from "./metrics";

/**
 * The date-range control.
 *
 * It reuses `ExpertRangeCalendar` — the same calendar the project page's film
 * window opens — rather than a Pro `<DateRangePicker>`. Two reasons, and the
 * second is the one that matters:
 *
 * 1. It is the house pattern for picking a range, so this panel and the film
 *    window behave identically instead of inventing a second grammar.
 *
 * 2. It is built on the FREE `StaticDatePicker`, hand-rolling range selection on
 *    a single calendar, so it never touches MUI X Pro's licence verifier. The
 *    Pro range pickers do, and that verifier THROWS on an expired annual key
 *    outside production (x-license-pro/utils/licenseErrorMessageUtils.js:33).
 *    The portal's key expired 2026-04-08, so rendering a Pro range picker here
 *    crashed the page — and because the router's root boundary turned the throw
 *    into a redirect, a video deep link presented as a 404.
 *
 *    It also explains why clicking a video worked while refreshing did not:
 *    `sharedLicenseStatuses` (useLicenseVerifier.js:8) caches the status BEFORE
 *    throwing and short-circuits every later call, so the error fires exactly
 *    ONCE per page load. The cold load threw and redirected, which primed the
 *    cache; clicking through afterwards was silent. Refresh, and it threw again.
 *
 * `ExpertRangeCalendar` is used as-is — not restyled, not forked. Its only other
 * consumer is `FilmWindowField`, and a change here would land on the project
 * page's film window.
 */

/** Shared with the panel's Source and Project selects so the row matches. */
export const CONTROL_SX = {
  height: 36,
  borderRadius: "10px",
  fontSize: 13,
  fontFamily: "inherit",
  background: "var(--surface)",
  color: "var(--text)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--track)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--muted)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent)" },
  "& .MuiSelect-icon": { color: "var(--muted)" },
} as const;

export const Labelled: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <label className="flex flex-col gap-[5px] min-w-0">
    <span
      className="font-CircularStdBlack uppercase text-muted"
      style={{ fontSize: 9.5, letterSpacing: ".12em" }}
    >
      {label}
    </span>
    {children}
  </label>
);

/** The Pro picker's shortcut panel, kept as visible buttons. */
const PRESETS: Array<{ label: string; of: (earliest: Dayjs | null) => ChartRange }> = [
  {
    label: "All time",
    of: (earliest) => [rangeFloor(earliest), dayjs()],
  },
  { label: "This week", of: () => [dayjs().startOf("week"), dayjs()] },
  {
    label: "Last week",
    of: () => {
      const prev = dayjs().subtract(7, "day");
      return [prev.startOf("week"), prev.endOf("week")];
    },
  },
  { label: "This month", of: () => [dayjs().startOf("month"), dayjs()] },
  {
    label: "Last month",
    of: () => {
      const prev = dayjs().subtract(1, "month");
      return [prev.startOf("month"), prev.endOf("month")];
    },
  },
];

const formatRange = ([start, end]: ChartRange): string =>
  start.isSame(end, "year")
    ? `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`
    : `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;

interface Props {
  range: ChartRange;
  earliest: Dayjs | null;
  onChange: (next: ChartRange) => void;
}

export const RangeControl: FC<Props> = ({ range, earliest, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The calendar reports an incomplete `[start, null]` mid-pick, and the panel's
  // range must always be a complete pair — so edits stay in a draft until Apply.
  const [draft, setDraft] = useState<DayRange>(range);

  useEffect(() => {
    if (anchor) setDraft(range);
  }, [anchor, range]);

  const complete = !!draft[0] && !!draft[1];
  const apply = () => {
    if (!complete) return;
    onChange([draft[0] as Dayjs, draft[1] as Dayjs]);
    setAnchor(null);
  };

  return (
    <>
      <Labelled label="Date range">
        <button
          type="button"
          onClick={(e) => setAnchor(e.currentTarget)}
          className="inline-flex items-center h-9 px-3 rounded-[10px] border bg-surface text-text-1 hover:border-muted transition-colors tabular-nums whitespace-nowrap shrink-0"
          style={{ borderColor: "var(--track)", fontSize: 13 }}
        >
          {formatRange(range)}
        </button>
      </Labelled>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "16px", p: 2, background: "var(--surface)" } }}
      >
        <div className="flex flex-wrap gap-1.5" style={{ maxWidth: 330, marginBottom: 12 }}>
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setDraft(preset.of(earliest))}
              className="inline-flex items-center h-7 px-2.5 rounded-full border border-border-token bg-surface text-text-1 hover:border-accent transition-colors"
              style={{ fontSize: 12 }}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <ExpertRangeCalendar
          value={draft}
          onChange={setDraft}
          // Both bounds are REQUIRED here. This calendar defaults to
          // `minDate || dayjs()` because its original job is scheduling a
          // future shoot — leaving it unset would disable every past day, which
          // is the only kind of day this panel has.
          minDate={rangeFloor(earliest)}
          maxDate={dayjs()}
          label="Select date range"
          caption="Views and watch time are counted inside this window."
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outlined" size="small" onClick={() => setAnchor(null)}>
            Cancel
          </Button>
          <Button size="small" disabled={!complete} onClick={apply}>
            Apply
          </Button>
        </div>
      </Popover>
    </>
  );
};

export default RangeControl;
